import type { ReactElement, FormEvent, ChangeEvent } from "react";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios, { AxiosError } from "axios";
import {
  Star,
  Sparkles,
  Mail,
  Lock,
  User,
  Eye,
  EyeOff,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Auth mode type
 */
type AuthMode = "login" | "register" | "forgot";

/**
 * Login response from API
 */
interface LoginResponse {
  session_token?: string;
  user?: {
    id: string;
    email: string;
    name: string;
  };
}

/**
 * Register response from API
 */
interface RegisterResponse {
  session_token?: string;
  email?: string;
  email_verification_required?: boolean;
}

/**
 * Forgot password response
 */
interface ForgotPasswordResponse {
  reset_token?: string;
  message?: string;
}

/**
 * Google OAuth URL response
 */
interface GoogleUrlResponse {
  url: string;
}

/**
 * API error response
 */
interface ApiErrorResponse {
  detail?: string;
}

/**
 * LoginPage Component
 *
 * Full page login/register/forgot password with Google OAuth.
 */
const LoginPage = (): ReactElement => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<AuthMode>("login");
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  // Form fields
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  const handleGoogleLogin = async (): Promise<void> => {
    try {
      const redirectUri = window.location.origin + "/auth/callback";
      // Get Google OAuth URL from our backend
      const response = await axios.get<GoogleUrlResponse>(
        `${API}/auth/google/url`,
        {
          params: { redirect_uri: redirectUri },
        }
      );
      // Redirect to Google's consent screen
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Failed to initiate Google login");
      console.error("Google login error:", error);
    }
  };

  const handleEmailLogin = async (
    e: FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post<LoginResponse>(
        `${API}/auth/login`,
        {
          email,
          password,
        },
        { withCredentials: true }
      );

      // Store session token in localStorage
      if (response.data.session_token) {
        localStorage.setItem("session_token", response.data.session_token);
      }

      toast.success("Welcome back!");
      navigate("/home");
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      // Check if email verification is required
      if (
        axiosError.response?.status === 403 &&
        axiosError.response?.headers?.["x-email-verification-required"]
      ) {
        toast.error("Please verify your email first");
        navigate("/verification-pending", { state: { email } });
        return;
      }
      toast.error(axiosError.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (
    e: FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();
    if (!email || !password || !name) {
      toast.error("Please fill in all fields");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post<RegisterResponse>(
        `${API}/auth/register`,
        {
          email,
          password,
          name,
        },
        { withCredentials: true }
      );

      // Check if email verification is required
      if (response.data.email_verification_required) {
        toast.success("Account created! Check your email to verify.");
        navigate("/verification-pending", {
          state: { email: response.data.email },
        });
        return;
      }

      // Fallback for if verification is disabled (shouldn't happen in production)
      if (response.data.session_token) {
        localStorage.setItem("session_token", response.data.session_token);
      }
      toast.success("Account created! You have 1 free story credit.");
      navigate("/home");
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      toast.error(axiosError.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (
    e: FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post<ForgotPasswordResponse>(
        `${API}/auth/forgot-password`,
        { email }
      );
      toast.success(
        "If an account exists, a reset link has been sent to your email"
      );

      // In development, show the reset token
      if (response.data.reset_token) {
        console.log("Reset token (dev only):", response.data.reset_token);
        // Store for reset page
        localStorage.setItem("reset_token", response.data.reset_token);
        navigate("/reset-password");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderLoginForm = (): ReactElement => (
    <form onSubmit={handleEmailLogin} className="space-y-4">
      <div>
        <label
          className="block text-sm font-semibold mb-2"
          style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
        >
          Email
        </label>
        <div className="relative">
          <Mail
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
            style={{ color: "var(--st-text-muted)" }}
          />
          <input
            type="email"
            data-testid="email-input"
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setEmail(e.target.value)
            }
            placeholder="your@email.com"
            className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 transition-all"
            style={{
              fontFamily: "Quicksand, sans-serif",
              border: "1px solid var(--st-border-light)",
              background: "var(--st-bg-surface)",
              color: "var(--st-text-heading)",
            }}
          />
        </div>
      </div>

      <div>
        <label
          className="block text-sm font-semibold mb-2"
          style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
        >
          Password
        </label>
        <div className="relative">
          <Lock
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
            style={{ color: "var(--st-text-muted)" }}
          />
          <input
            type={showPassword ? "text" : "password"}
            data-testid="password-input"
            value={password}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setPassword(e.target.value)
            }
            placeholder="••••••••"
            className="w-full pl-10 pr-10 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 transition-all"
            style={{
              fontFamily: "Quicksand, sans-serif",
              border: "1px solid var(--st-border-light)",
              background: "var(--st-bg-surface)",
              color: "var(--st-text-heading)",
            }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2"
          >
            {showPassword ? (
              <EyeOff className="w-4 h-4" style={{ color: "var(--st-text-muted)" }} />
            ) : (
              <Eye className="w-4 h-4" style={{ color: "var(--st-text-muted)" }} />
            )}
          </button>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setMode("forgot")}
          className="text-sm font-semibold"
          style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-primary)" }}
        >
          Forgot password?
        </button>
      </div>

      <button
        type="submit"
        data-testid="login-btn"
        disabled={loading}
        className="w-full py-2.5 rounded-lg font-semibold text-sm transition-all disabled:opacity-50"
        style={{
          background: "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
          color: "white",
          fontFamily: "Quicksand, sans-serif",
        }}
      >
        {loading ? "Logging in..." : "Login"}
      </button>

      <p
        className="text-center text-sm"
        style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
      >
        New here?{" "}
        <button
          type="button"
          onClick={() => setMode("register")}
          className="font-bold"
          style={{ color: "var(--st-primary)" }}
        >
          Sign up
        </button>
      </p>
    </form>
  );

  const renderRegisterForm = (): ReactElement => (
    <form onSubmit={handleRegister} className="space-y-4">
      <div>
        <label
          className="block text-sm font-semibold mb-2"
          style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
        >
          Name
        </label>
        <div className="relative">
          <User
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
            style={{ color: "var(--st-text-muted)" }}
          />
          <input
            type="text"
            data-testid="name-input"
            value={name}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setName(e.target.value)
            }
            placeholder="Your name"
            className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 transition-all"
            style={{
              fontFamily: "Quicksand, sans-serif",
              border: "1px solid var(--st-border-light)",
              background: "var(--st-bg-surface)",
              color: "var(--st-text-heading)",
            }}
          />
        </div>
      </div>

      <div>
        <label
          className="block text-sm font-semibold mb-2"
          style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
        >
          Email
        </label>
        <div className="relative">
          <Mail
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
            style={{ color: "var(--st-text-muted)" }}
          />
          <input
            type="email"
            data-testid="register-email-input"
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setEmail(e.target.value)
            }
            placeholder="your@email.com"
            className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 transition-all"
            style={{
              fontFamily: "Quicksand, sans-serif",
              border: "1px solid var(--st-border-light)",
              background: "var(--st-bg-surface)",
              color: "var(--st-text-heading)",
            }}
          />
        </div>
      </div>

      <div>
        <label
          className="block text-sm font-semibold mb-2"
          style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
        >
          Password
        </label>
        <div className="relative">
          <Lock
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
            style={{ color: "var(--st-text-muted)" }}
          />
          <input
            type={showPassword ? "text" : "password"}
            data-testid="register-password-input"
            value={password}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setPassword(e.target.value)
            }
            placeholder="Min 6 characters"
            className="w-full pl-10 pr-10 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 transition-all"
            style={{
              fontFamily: "Quicksand, sans-serif",
              border: "1px solid var(--st-border-light)",
              background: "var(--st-bg-surface)",
              color: "var(--st-text-heading)",
            }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2"
          >
            {showPassword ? (
              <EyeOff className="w-4 h-4" style={{ color: "var(--st-text-muted)" }} />
            ) : (
              <Eye className="w-4 h-4" style={{ color: "var(--st-text-muted)" }} />
            )}
          </button>
        </div>
      </div>

      <div>
        <label
          className="block text-sm font-semibold mb-2"
          style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
        >
          Confirm Password
        </label>
        <div className="relative">
          <Lock
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
            style={{ color: "var(--st-text-muted)" }}
          />
          <input
            type={showPassword ? "text" : "password"}
            data-testid="confirm-password-input"
            value={confirmPassword}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setConfirmPassword(e.target.value)
            }
            placeholder="Confirm password"
            className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 transition-all"
            style={{
              fontFamily: "Quicksand, sans-serif",
              border: "1px solid var(--st-border-light)",
              background: "var(--st-bg-surface)",
              color: "var(--st-text-heading)",
            }}
          />
        </div>
      </div>

      <button
        type="submit"
        data-testid="register-btn"
        disabled={loading}
        className="w-full py-2.5 rounded-lg font-semibold text-sm transition-all disabled:opacity-50"
        style={{
          background: "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
          color: "white",
          fontFamily: "Quicksand, sans-serif",
        }}
      >
        {loading ? "Creating account..." : "Create Account"}
      </button>

      <p
        className="text-center text-sm"
        style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
      >
        Already have an account?{" "}
        <button
          type="button"
          onClick={() => setMode("login")}
          className="font-bold"
          style={{ color: "var(--st-primary)" }}
        >
          Login
        </button>
      </p>
    </form>
  );

  const renderForgotForm = (): ReactElement => (
    <form onSubmit={handleForgotPassword} className="space-y-4">
      <button
        type="button"
        onClick={() => setMode("login")}
        className="flex items-center gap-2 text-sm font-semibold mb-4"
        style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
      >
        <ArrowLeft className="w-4 h-4" />
        Back to login
      </button>

      <p
        className="text-sm mb-4"
        style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
      >
        Enter your email address and we will send you a link to reset your
        password.
      </p>

      <div>
        <label
          className="block text-sm font-semibold mb-2"
          style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
        >
          Email
        </label>
        <div className="relative">
          <Mail
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
            style={{ color: "var(--st-text-muted)" }}
          />
          <input
            type="email"
            data-testid="forgot-email-input"
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setEmail(e.target.value)
            }
            placeholder="your@email.com"
            className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 transition-all"
            style={{
              fontFamily: "Quicksand, sans-serif",
              border: "1px solid var(--st-border-light)",
              background: "var(--st-bg-surface)",
              color: "var(--st-text-heading)",
            }}
          />
        </div>
      </div>

      <button
        type="submit"
        data-testid="reset-request-btn"
        disabled={loading}
        className="w-full py-2.5 rounded-lg font-semibold text-sm transition-all disabled:opacity-50"
        style={{
          background: "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
          color: "white",
          fontFamily: "Quicksand, sans-serif",
        }}
      >
        {loading ? "Sending..." : "Send Reset Link"}
      </button>
    </form>
  );

  const getTitle = (): string => {
    switch (mode) {
      case "register":
        return "Create Account";
      case "forgot":
        return "Reset Password";
      default:
        return "Welcome Back";
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{
        background:
          "linear-gradient(to bottom, var(--st-bg-page) 0%, var(--st-border-light) 50%, var(--st-border-medium) 100%)",
      }}
    >
      <div className="container mx-auto px-4 max-w-md">
        {/* Floating decorations */}
        <div className="absolute top-10 left-10 animate-float">
          <Star className="w-8 h-8 text-yellow-400 fill-yellow-400 animate-sparkle" />
        </div>
        <div
          className="absolute top-20 right-16 animate-float"
          style={{ animationDelay: "1s" }}
        >
          <Sparkles className="w-6 h-6 text-purple-400 animate-sparkle" />
        </div>

        {/* Card */}
        <div className="card-warm animate-slide-up">
          <div className="text-center mb-6">
            <div className="mb-4">
              <Logo size={128} animate className="mx-auto" />
            </div>

            <h1
              className="text-2xl font-bold mb-1"
              style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
            >
              {getTitle()}
            </h1>

            {mode === "login" && (
              <p
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
              >
                Sign in to continue your story journey
              </p>
            )}
            {mode === "register" && (
              <p
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
              >
                Get 1 free story when you sign up!
              </p>
            )}
          </div>

          {/* Form */}
          {mode === "login" && renderLoginForm()}
          {mode === "register" && renderRegisterForm()}
          {mode === "forgot" && renderForgotForm()}

          {/* Divider */}
          {mode !== "forgot" && (
            <>
              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px" style={{ background: "var(--st-border-medium)" }}></div>
                <span
                  className="text-sm font-semibold"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    color: "var(--st-text-muted)",
                  }}
                >
                  or
                </span>
                <div className="flex-1 h-px" style={{ background: "var(--st-border-medium)" }}></div>
              </div>

              {/* Google Login */}
              <button
                data-testid="google-login-btn"
                onClick={handleGoogleLogin}
                className="w-full flex items-center justify-center gap-3 py-2.5 rounded-lg font-semibold text-sm active:scale-95 transition-all shadow-sm"
                style={{
                  background: "var(--st-bg-surface)",
                  color: "var(--st-text-heading)",
                  fontFamily: "Quicksand, sans-serif",
                  border: "1px solid var(--st-border-light)",
                }}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>
            </>
          )}

          {/* Skip */}
          <button
            data-testid="skip-login-btn"
            onClick={() => navigate("/")}
            className="w-full mt-4 text-sm font-semibold"
            style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
